import React, { useState } from 'react';
import { Box, Spacer, Text } from '@zillow/constellation-10';
import { Center } from '@zg-rentals/zrs-comet';
import {
  HeadWrapper,
  LoadingConstants,
  SpinnerContainer,
  useIsScreenDesktop,
  useRentalManagerUser,
} from '@zg-rentals/rental-manager-env';
import { useRentalManagerUserManagementCompanyWebApiHandler_getUserCompanyStats } from '@zg-rentals/ts-rental-manager-api-client-react-query';

export const COMPANY_STATS_TAG = 'companyStats';
import { AdvancedTable } from '@zg-rentals/advanced-table';
import PropertyListModal from '../../modal/UserManagementPropertyListModal';
import ResendCompanyInviteModal from '../../modal/ResendCompanyInviteModal';
import columnConfigs from './columnConfigs';
import { useActiveTab } from '../../hooks/useActiveTab';
import { styledUsersPageStyles } from './StyledComponents';
import UserManagementActions from '../../redux/actions/UserManagementActions';
import type { UserState } from '../../hooks/userStates';
import type { UserManagementUserProfileDto } from '@zg-rentals/ts-user-management-client';

interface UsersPageProps {
  userState: UserState;
}

const UsersPage = ({ userState }: UsersPageProps) => {
  const activeTab = useActiveTab();
  const {
    data: company,
    isLoading: isCompanyLoading,
    error: companyError,
  } = useRentalManagerUserManagementCompanyWebApiHandler_getUserCompanyStats({
    meta: { tags: [COMPANY_STATS_TAG], context: { api: 'rental-manager-api', details: 'getUserCompanyStats' } },
  });
  const isScreenDesktop = useIsScreenDesktop();
  const { user } = useRentalManagerUser();
  const [showResendInviteModal, setShowResendInviteModal] = useState(false);
  const [invitedUser, setInvitedUser] = useState() as [
    UserManagementUserProfileDto,
    (user: UserManagementUserProfileDto) => void,
  ];
  const [loadProperties, setLoadProperties] = useState();
  const [showPropertyListModal, setShowPropertyListModal] = useState(false);

  const handleResendInvite = (invitedUser: UserManagementUserProfileDto) => {
    setInvitedUser(invitedUser);
    setShowResendInviteModal(true);
  };

  const previewUserProperties = (userBrand: string, userToken: string, onlyUnpaidProperties = false) => {
    const onLoadProperties = () =>
      UserManagementActions.getCurrentUserProperties(userBrand, userToken, onlyUnpaidProperties);
    // @ts-ignore
    setLoadProperties(onLoadProperties);
    setShowPropertyListModal(true);
  };

  const previewPendingProperties = (email: string, onlyUnpaidProperties = false) => {
    const onLoadProperties = () => UserManagementActions.getPendingUserProperties(email, onlyUnpaidProperties);
    // @ts-ignore
    setLoadProperties(onLoadProperties);
    setShowPropertyListModal(true);
  };

  const columnConfig = columnConfigs.getConfigs({
    isScreenMobile: !isScreenDesktop,
    onResendInvite: handleResendInvite,
    yourToken: user.userToken,
    activeTab,
    companyUsers: userState,
    previewUserProperties,
    previewPendingProperties,
  });

  const isLoading =
    isCompanyLoading ||
    userState.loadingState === LoadingConstants.LOADING ||
    userState.loadingState === LoadingConstants.INITIAL;

  const renderEmptyTableMessage = () => {
    if (userState.profiles.length !== 0 || isLoading) {
      return null;
    }
    return (
      <Spacer css={{ marginBlock: 'xl' }}>
        <Center>No Users Found</Center>
      </Spacer>
    );
  };

  const renderContent = () => {
    if (userState.loadingState === LoadingConstants.ERROR || companyError) {
      return (
        <Text css={{ color: 'red.600' }} asChild>
          <div>Cannot retrieve user management information. Please try again later.</div>
        </Text>
      );
    }

    return (
      <React.Fragment>
        <AdvancedTable columnConfig={columnConfig} data={userState.profiles} />
        {renderEmptyTableMessage()}
        {showResendInviteModal && (
          <ResendCompanyInviteModal setShowResendInviteModal={setShowResendInviteModal} invitedUser={invitedUser} />
        )}
        {showPropertyListModal && (
          <PropertyListModal onLoadProperties={loadProperties} onClose={() => setShowPropertyListModal(false)} />
        )}
      </React.Fragment>
    );
  };

  return (
    <Box className={styledUsersPageStyles}>
      <HeadWrapper title="Users" />
      {isLoading ? <SpinnerContainer /> : renderContent()}
    </Box>
  );
};

export default UsersPage;
